/* @flow */
import invariant from 'invariant';
import { ActionTypes } from 'groups/constants/GroupConstants';
import Group from 'entities/Group';
import Groups from 'collections/Groups';
import { PushEvents } from 'pusher/PusherConstants';
import Message from 'entities/Message';
import GroupActions from 'groups/actions/GroupActions';
import { rootGroup } from 'entities/RootGroup';
import Store from 'lib/Store';
import FeatureStore from 'stores/FeatureStore';
import { touch } from 'actions/touchActions';
import { TOUCH_KEY as NAV_TOUCH_KEY } from 'components/NavigationList/NavigationList';

// Validate that changes contain valid uuids
// for https://rollbar.com/Remind/r101-apollo/items/43656/
function pusherChangesUuids(action: *) {
  const { changes } = action;
  const uuids = changes.map(change => change.uuid).filter(Boolean);
  if (uuids.length !== changes.length) {
    console.warn({ action });
    console.error('pusherChangesUuids got changes with empty uuid');
  }
  return uuids;
}

/**
 * All the current user's groups.
 *
 * NOTE that groups that are loaded from the server will not necessarily be
 * stored here until they become one of the user's groups.
 *
 * Note: Flux stores are now a DEPRECATED pattern. See docs/GraphQL.md
 */
class GroupStore extends Store {
  getInitialState() {
    return {
      archivedLoaded: false,
      loaded: false,
      groupCodeToId: {},
      groupNameToId: {},
      groups: new Groups(),
      groupsJoinedDuringSetup: [],
    };
  }

  setGroupCreatedDuringSetup(group: Group): void {
    this.state.groupCreatedDuringSetup = group;
  }

  getGroupCreatedDuringSetup() {
    return this.state.groupCreatedDuringSetup;
  }

  clearGroupCreatedDuringSetup(): void {
    delete this.state.groupCreatedDuringSetup;
  }

  addToGroupsJoinedDuringSetup(group: Group): void {
    this.state.groupsJoinedDuringSetup.push(group);
  }

  getGroupsJoinedDuringSetup(): Group[] {
    return this.state.groupsJoinedDuringSetup;
  }

  clearGroupsJoinedDuringSetup(): void {
    this.state.groupsJoinedDuringSetup = [];
  }

  // TODO: Improve. This is a cheap implementation to get around some loading issues.
  // See https://github.com/remind101/r101-frontend/issues/339
  isLoaded(): boolean {
    return this.state.loaded;
  }

  isArchivedLoaded(): boolean {
    return this.state.archivedLoaded;
  }

  invalidate(): void {
    this.state.loaded = false;
  }

  isConnectedToGroup(group: Group): boolean {
    return this.getGroup(group.id) !== undefined;
  }

  /**
   * Filters out archived groups.
   */
  getGroups(): Groups {
    const unarchivedGroups = this.state.groups.filter(group => !group.isArchived());
    return new Groups(unarchivedGroups);
  }

  selectGroups(predicate: Function): Groups {
    const selectedItems = this.state.groups.filter(predicate);
    return new Groups(selectedItems);
  }

  getOwnedGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'owned' && !group.isArchived());
  }

  getInvitableClasses(): Groups {
    return this.selectGroups(
      group => ['owned', 'subscribed'].includes(group.getType()) && group.canAddPeople()
    );
  }

  getAdministratedGroups(): Groups {
    return this.selectGroups(
      group => group.isStaffClass() && group.getPostableRoles().includes('admin')
    );
  }

  getAdministratedGroupByOrganizationId(organizationID: number) {
    return this.getAdministratedGroups().find(group => {
      return group.getStaffClassOrgId() === organizationID;
    });
  }

  getInvitableGroups(): Groups {
    return this.selectGroups(group => group.canAddPeople());
  }

  hasOwnedGroups(): boolean {
    return this.getOwnedGroups().length > 0;
  }

  getSubscribedGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'subscribed');
  }

  getSchoolGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'organization');
  }

  getDistrictGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'district');
  }

  getGroupsAtOrganization(orgId: Backbone$Id): Groups {
    return this.selectGroups(
      g => g.getOrganization() && g.getOrganization().id === orgId && !g.isStaffClass()
    );
  }

  /**
   * @deprecated use postable roles instead
   */
  getPostableGroups(): Groups {
    return this.selectGroups(group => group.canPost());
  }

  getGroup(groupId: Backbone$Id): ?Group {
    return this.state.groups.get(groupId);
  }

  getGroupForCode(groupCode: string): ?Group {
    if (groupCode === rootGroup.getCode()) {
      return rootGroup;
    }

    const id = this.state.groupCodeToId[groupCode];
    if (id) {
      return this.getGroup(id);
    }

    return null;
  }

  getOwnedGroupForName(groupName: string): ?Group {
    return this.getOwnedGroups().find(g => g.getName() === groupName);
  }

  getStaffClassForOrg(orgId: Backbone$Id): ?Group {
    return this.state.groups.find(g => g.getStaffClassOrgId() === orgId);
  }

  getArchivedGroups(): Group[] {
    return this.state.groups.filter(group => group.isArchived());
  }

  /* This is called when group is added/updated or deleted
   * because is the group code or group name is updated
   * then we need to flush the old value.
   */
  resetMappings(): void {
    this.state.groupCodeToId = [];
    this.state.groupNameToId = [];
    this.state.archivedGroupCodeToId = [];

    this.state.groups.forEach(group => {
      // 'name' is the legacy attribute. getCode() always returns a string but
      // this allows it to null like this code was written for. What will
      // happen is that groupCodeToId gets a prop "null" due to JS type
      // coercion.
      this.state.groupCodeToId[group.get('name')] = group.id;
      this.state.groupNameToId[group.getName().toLowerCase()] = group.id;
    });
  }

  validateLastReadSeq(oldGroup: Group, newGroup: Group): boolean {
    const oldSeq = oldGroup.getLastReadSeq();
    const newSeq = newGroup.getLastReadSeq();
    return !oldSeq || !newSeq || newSeq >= oldSeq;
  }

  addGroup(group: Group): void {
    const oldGroup = this.state.groups.get(group);
    if (!oldGroup || this.validateLastReadSeq(oldGroup, group)) {
      this.state.groups.remove(group); // force .add to add new ref in collection
      this.state.groups.add(group);
    }
  }

  deleteGroup(group: Group): void {
    this.state.groups.remove(group);
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case ActionTypes.RESET_STORE:
        this.resetState();
        break;
      case ActionTypes.UPDATE_GROUP:
      case ActionTypes.LOAD_GROUP:
        // to handle pusherman events for when you get approved for a request to join class (RTJ)
        touch(NAV_TOUCH_KEY);
        this.handleLoadGroups([action.group]);
        break;
      case ActionTypes.RESIGN_OWNERSHIP:
        this.handleDeleteGroups([action.group]);
        break;
      case ActionTypes.LIST_GROUPS:
        if (action.allMine) {
          this.handleSetGroups(action.groups);
        } else {
          this.handleLoadGroups(action.groups);
        }
        break;
      case ActionTypes.CREATE_GROUP:
      case ActionTypes.GROUPS_UPDATED:
        this.handleUpdateGroups(action.groups);
        break;
      case ActionTypes.LIST_ARCHIVED_GROUPS:
        this.handleLoadGroups(action.groups);
        this.state.archivedLoaded = true;
        break;
      case ActionTypes.ARCHIVE:
        this.handleUpdateGroups([action.group]);
        break;
      case ActionTypes.ARCHIVE_AND_REUSE:
        this.handleUpdateGroups([action.group, action.archivedGroup]);
        break;
      case ActionTypes.REUSE:
        this.handleUpdateGroups([action.group, action.archivedGroup]);
        break;
      case ActionTypes.RESTORE:
        this.handleUpdateGroups([action.group]);
        break;
      case PushEvents.CHANGESET_DESTROY_GROUP: {
        const uuids = pusherChangesUuids(action);
        const groupsToRemove = this.state.groups.filter(group => {
          return uuids.includes(group.getUuid());
        });
        groupsToRemove.forEach(group => this.state.groups.remove(group));
        touch(NAV_TOUCH_KEY);
        this.resetMappings();
        this.emitChange();
        break;
      }
      case PushEvents.CHANGESET_CREATE_GROUP:
      case PushEvents.CHANGESET_UPDATE_GROUP: {
        const uuids = pusherChangesUuids(action);
        GroupActions.loadGroups(uuids);
        break;
      }
      case ActionTypes.SUBSCRIBED:
        this.handleLoadGroups([action.group]);
        break;
      case ActionTypes.UNSUBSCRIBED:
        this.handleDeleteGroups([action.group]);
        break;
      case ActionTypes.SEND_GROUP_MESSAGE:
        this.handleMessageSent(action.message);
        break;
      case ActionTypes.JOIN_GROUP_DURING_SETUP:
        this.addToGroupsJoinedDuringSetup(action.group);
        break;
      case ActionTypes.CREATE_GROUP_DURING_SETUP:
        this.setGroupCreatedDuringSetup(action.group);
        break;
      case ActionTypes.CLEAR_GROUP_CREATED_DURING_SETUP:
        this.clearGroupCreatedDuringSetup();
        break;
      case ActionTypes.CLEAR_GROUPS_JOINED_DURING_SETUP:
        this.clearGroupsJoinedDuringSetup();
        break;
      default:
        break;
    }
  }

  handleSetGroups(groups: Group[]): void {
    const ids = groups.map(group => group.id);
    const toRemove = this.state.groups.filter(group => !ids.includes(group.id));

    groups.forEach(group => this.addGroup(group));
    toRemove.forEach(group => this.deleteGroup(group));

    this.resetMappings();
    this.state.loaded = true;
    this.emitChange();
  }

  handleLoadGroups(groups: Group[]): void {
    groups.forEach(group => this.addGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleDeleteGroups(groups: Group[]): void {
    groups.forEach(group => this.deleteGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleUpdateGroups(groups: Group[]): void {
    groups.forEach(group => this.addGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleMessageSent(message: Message): void {
    if (FeatureStore.hasFeature('classes_v2_removal.web.aug-19')) {
      this.emitChange();
      return;
    }

    const threads = message.getGroupThreads();
    threads.forEach(t => {
      invariant(t.group_id, 'handleMessageSent received a message without a group id or role');
      const group = this.getGroup(t.group_id);
      if (group) {
        const groupThreads = group.getThreads();
        groupThreads.forEach(gt => {
          if (gt.role === t.role) {
            group.setThreadPreview(gt.role, message.getPreview(), message.getSentAt());
          }
        });
      }
    });
    this.emitChange();
  }
}

export default new GroupStore();
